import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import './Leads.css';

const STATUS_OPTIONS = [
  'ALL',
  'NEW',
  'ASSIGNED',
  'CONTACTED',
  'FOLLOW_UP',
  'NOT_INTERESTED',
  'CONVERTED',
  'CLOSED',
];

const STATUS_LABELS = {
  NEW: 'New',
  ASSIGNED: 'Assigned',
  CONTACTED: 'Contacted',
  FOLLOW_UP: 'Follow Up',
  NOT_INTERESTED: 'Not Interested',
  CONVERTED: 'Converted',
  CLOSED: 'Closed',
};

const LEAD_SCORE_OPTIONS = [
  { label: 'All Scores', value: 'ALL' },
  { label: '0 Calls (Cold)', value: '0-0' },
  { label: '1–3 Calls', value: '1-3' },
  { label: '4–10 Calls', value: '4-10' },
  { label: '10+ Calls (Hot)', value: '10-999999' },
];

export default function Leads({ auth }) {
  const canManage = auth?.role === 'ADMIN' || auth?.role === 'MANAGER';

  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterScore, setFilterScore] = useState('ALL');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterSource, setFilterSource] = useState('ALL');

  const [page, setPage] = useState(0);
  const [size] = useState(10);

  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLeadId, setDeleteLeadId] = useState(null);

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);

  // CSV Import State
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [csvError, setCsvError] = useState('');
  const csvInputRef = useRef(null);

  // Show more filters toggle
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const [leadForm, setLeadForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    altPhone: '',
    email: '',
    company: '',
    city: '',
    state: '',
    address: '',
    category: '',
    rating: '',
    openHours: '',
    website: '',
    facebook: '',
    instagram: '',
    twitter: '',
    emails: '',
    mapUrl: '',
    notes: '',
    status: 'NEW',
  });

  useEffect(() => {
    loadLeads();
  }, [page, searchQuery, filterStatus, filterScore, filterCategory, filterSource]);

  useEffect(() => {
    if (canManage) {
      loadAgents();
    }
  }, [canManage]);

  const loadLeads = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page,
        size,
      });

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      if (filterStatus !== 'ALL') {
        params.append('status', filterStatus);
      }

      if (filterScore !== 'ALL') {
        const [min, max] = filterScore.split('-');
        params.append('minScore', min);
        params.append('maxScore', max);
      }

      if (filterCategory !== 'ALL') {
        params.append('category', filterCategory);
      }

      if (filterSource !== 'ALL') {
        params.append('source', filterSource);
      }

      const response = await api.get(`/api/leads?${params.toString()}`);

      const data = response.data;

      setLeads(data?.content || data || []);
      setTotalPages(data?.totalPages || 0);
      setTotalElements(data?.totalElements || 0);

      setSelectedLeads([]);
    } catch (err) {
      console.error(err);
      setError('Unable to load leads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await api.get('/api/users/role/AGENT');
      setAgents(response.data || []);
    } catch (err) {
      console.error('Unable to load agents', err);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleStatusFilter = (e) => {
    setFilterStatus(e.target.value);
    setPage(0);
  };

  const handleScoreFilter = (e) => {
    setFilterScore(e.target.value);
    setPage(0);
  };

  const handleCategoryFilter = (e) => {
    setFilterCategory(e.target.value);
    setPage(0);
  };

  const handleSourceFilter = (e) => {
    setFilterSource(e.target.value);
    setPage(0);
  };

  const toggleSelectLead = (leadId) => {
    if (!canManage) return;
    setSelectedLeads((current) =>
      current.includes(leadId)
        ? current.filter((id) => id !== leadId)
        : [...current, leadId]
    );
  };

  const toggleSelectAll = () => {
    if (!canManage) return;
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map((lead) => lead.leadId));
    }
  };

  const openAddLead = () => {
    if (!canManage) return;
    setEditingLead(null);
    setLeadForm({
      firstName: '',
      lastName: '',
      phone: '',
      altPhone: '',
      email: '',
      company: '',
      city: '',
      state: '',
      address: '',
      category: '',
      rating: '',
      openHours: '',
      website: '',
      facebook: '',
      instagram: '',
      twitter: '',
      emails: '',
      mapUrl: '',
      notes: '',
      status: 'NEW',
    });
    setShowLeadModal(true);
  };

  const openEditLead = (lead) => {
    if (!canManage) return;
    setEditingLead(lead);

    setLeadForm({
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      phone: lead.phone || '',
      altPhone: lead.altPhone || '',
      email: lead.email || '',
      company: lead.company || '',
      city: lead.city || '',
      state: lead.state || '',
      address: lead.address || '',
      category: lead.category || '',
      rating: lead.rating || '',
      openHours: lead.openHours || '',
      website: lead.website || '',
      facebook: lead.facebook || '',
      instagram: lead.instagram || '',
      twitter: lead.twitter || '',
      emails: lead.emails || '',
      mapUrl: lead.mapUrl || '',
      notes: lead.notes || '',
      status: lead.status || 'NEW',
    });

    setShowLeadModal(true);
  };

  const handleLeadFormChange = (e) => {
    const { name, value } = e.target;

    setLeadForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const saveLead = async (e) => {
    e.preventDefault();
    if (!canManage) return;

    try {
      if (editingLead) {
        await api.put(`/api/leads/${editingLead.leadId}`, leadForm);
      } else {
        await api.post('/api/leads', leadForm);
      }

      setShowLeadModal(false);
      await loadLeads();
    } catch (err) {
      console.error(err);
      alert('Unable to save lead.');
    }
  };

  const confirmDelete = (leadId) => {
    if (!canManage) return;
    setDeleteLeadId(leadId);
    setShowDeleteModal(true);
  };

  const deleteLead = async () => {
    if (!deleteLeadId || !canManage) return;

    try {
      await api.post('/api/leads/bulk-delete', {
        leadIds: [deleteLeadId],
      });

      setShowDeleteModal(false);
      setDeleteLeadId(null);

      await loadLeads();
    } catch (err) {
      console.error(err);
      alert('Unable to delete lead.');
    }
  };

  const openAssignModal = () => {
    if (!canManage) return;
    if (selectedLeads.length === 0) {
      alert('Select at least one lead.');
      return;
    }

    setSelectedAgent('');
    setShowAssignModal(true);
  };

  const assignLeads = async () => {
    if (!canManage) return;
    if (!selectedAgent) {
      alert('Please select an agent.');
      return;
    }

    try {
      if (selectedLeads.length === 1) {
        await api.post('/api/assignments', {
          leadId: selectedLeads[0],
          assignedTo: Number(selectedAgent),
        });
      } else {
        await api.post('/api/assignments/bulk', {
          leadIds: selectedLeads,
          assignedTo: Number(selectedAgent),
        });
      }

      setShowAssignModal(false);
      setSelectedAgent('');
      setSelectedLeads([]);

      await loadLeads();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown error';
      alert('Unable to assign leads. Details: ' + JSON.stringify(err.response?.data || msg));
    }
  };

  const assignRoundRobin = async () => {
    if (!canManage) return;
    if (selectedLeads.length === 0) {
      alert('Select at least one lead.');
      return;
    }

    try {
      await api.post('/api/assignments/round-robin', {
        leadIds: selectedLeads,
        agentIds: agents.map(a => a.userId || a.id)
      });

      setSelectedLeads([]);
      await loadLeads();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown error';
      alert('Unable to assign leads using round-robin. Details: ' + JSON.stringify(err.response?.data || msg));
    }
  };

  // ---- CSV Import Functions ----

  const openCsvImport = () => {
    setCsvFile(null);
    setCsvPreview(null);
    setCsvResult(null);
    setCsvError('');
    setCsvImporting(false);
    setShowCsvModal(true);
  };

  const handleCsvFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFile(file);
    setCsvPreview(null);
    setCsvError('');
    setCsvResult(null);

    // Preview the file
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/api/leads/preview-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCsvPreview(response.data);
    } catch (err) {
      setCsvError(err.response?.data?.error || 'Failed to parse CSV file.');
    }
  };

  const confirmCsvImport = async () => {
    if (!csvFile) return;

    setCsvImporting(true);
    setCsvError('');
    setCsvResult(null);

    const formData = new FormData();
    formData.append('file', csvFile);

    try {
      const response = await api.post('/api/leads/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCsvResult(response.data);
      await loadLeads();
    } catch (err) {
      setCsvError(err.response?.data?.error || 'Failed to import CSV.');
    } finally {
      setCsvImporting(false);
    }
  };

  const formatStatus = (status) => {
    return STATUS_LABELS[status] || status || 'Unknown';
  };

  const getStatusClass = (status) => {
    return `lead-status lead-status-${String(status || '')
      .toLowerCase()
      .replace(/_/g, '-')}`;
  };

  const getScoreBadgeClass = (score) => {
    if (score === 0) return 'score-badge score-cold';
    if (score <= 3) return 'score-badge score-warm';
    if (score <= 10) return 'score-badge score-hot';
    return 'score-badge score-fire';
  };

  const getScoreLabel = (score) => {
    if (score === 0) return 'Cold';
    if (score <= 3) return 'Warm';
    if (score <= 10) return 'Hot';
    return '🔥 Fire';
  };

  // Get unique categories and sources from current leads for filter dropdowns
  const uniqueCategories = [...new Set(leads.map(l => l.category).filter(Boolean))];
  const uniqueSources = [...new Set(leads.map(l => l.source).filter(Boolean))];

  return (
    <div className="leads-page">
      <div className="leads-header">
        <div>
          <div className="page-eyebrow">CRM</div>
          <h1>Leads</h1>
          <p>Browse and track your sales pipeline leads.</p>
        </div>

        {canManage && (
          <div className="leads-header-actions">
            <button className="csv-import-btn" onClick={openCsvImport}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Import CSV
            </button>
            <button className="primary-action-btn" onClick={openAddLead}>
              <span>+</span>
              Add Lead
            </button>
          </div>
        )}
      </div>

      <div className="leads-toolbar">
        <div className="lead-search-box">
          <span className="lead-search-icon">⌕</span>

          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search leads by name, phone, company..."
          />

          {searchQuery && (
            <button
              className="clear-search-btn"
              onClick={() => {
                setSearchQuery('');
                setPage(0);
              }}
            >
              ×
            </button>
          )}
        </div>

        <div className="lead-filter-box">
          <select value={filterStatus} onChange={handleStatusFilter}>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status === 'ALL' ? 'All Statuses' : formatStatus(status)}
              </option>
            ))}
          </select>
        </div>

        <div className="lead-filter-box">
          <select value={filterScore} onChange={handleScoreFilter}>
            {LEAD_SCORE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <button
          className={`filter-toggle-btn ${showMoreFilters ? 'active' : ''}`}
          onClick={() => setShowMoreFilters(!showMoreFilters)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="21" x2="4" y2="14" />
            <line x1="4" y1="10" x2="4" y2="3" />
            <line x1="12" y1="21" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12" y2="3" />
            <line x1="20" y1="21" x2="20" y2="16" />
            <line x1="20" y1="12" x2="20" y2="3" />
            <line x1="1" y1="14" x2="7" y2="14" />
            <line x1="9" y1="8" x2="15" y2="8" />
            <line x1="17" y1="16" x2="23" y2="16" />
          </svg>
          More Filters
        </button>

        {canManage && (
          <div className="lead-management-actions">
            <button
              className="secondary-action-btn"
              onClick={openAssignModal}
              disabled={selectedLeads.length === 0}
            >
              Assign
            </button>

            <button
              className="secondary-action-btn"
              onClick={assignRoundRobin}
              disabled={selectedLeads.length === 0}
            >
              Round Robin
            </button>
          </div>
        )}
      </div>

      {/* More Filters Row */}
      {showMoreFilters && (
        <div className="more-filters-row">
          <div className="lead-filter-box">
            <label className="filter-label">Category</label>
            <select value={filterCategory} onChange={handleCategoryFilter}>
              <option value="ALL">All Categories</option>
              {uniqueCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="lead-filter-box">
            <label className="filter-label">Source</label>
            <select value={filterSource} onChange={handleSourceFilter}>
              <option value="ALL">All Sources</option>
              {uniqueSources.map((src) => (
                <option key={src} value={src}>{src}</option>
              ))}
            </select>
          </div>

          <button
            className="clear-filters-btn"
            onClick={() => {
              setFilterStatus('ALL');
              setFilterScore('ALL');
              setFilterCategory('ALL');
              setFilterSource('ALL');
              setPage(0);
            }}
          >
            Clear All Filters
          </button>
        </div>
      )}

      <div className="leads-summary-row">
        <span>
          <strong>{totalElements}</strong> total leads
        </span>

        {selectedLeads.length > 0 && canManage && (
          <span className="selected-count">
            {selectedLeads.length} selected
          </span>
        )}
      </div>

      {error && (
        <div className="leads-error">
          <span>{error}</span>
          <button onClick={loadLeads}>Retry</button>
        </div>
      )}

      <div className="leads-table-card">
        <div className="leads-table-wrapper">
          <table className="leads-table">
            <thead>
              <tr>
                {canManage && (
                  <th className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={
                        leads.length > 0 &&
                        selectedLeads.length === leads.length
                      }
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}

                <th>Lead</th>
                <th>Company</th>
                <th>Phone</th>
                <th>City</th>
                <th>Status</th>
                <th>Lead Score</th>
                <th>Assigned Agent</th>
                <th className="actions-column">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={canManage ? 9 : 8}
                    className="table-loading"
                  >
                    <div className="table-loader"></div>
                    Loading leads...
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td
                    colSpan={canManage ? 9 : 8}
                    className="empty-leads"
                  >
                    <div className="empty-leads-icon">◎</div>
                    <strong>No leads found</strong>
                    <span>
                      Try changing your search or filter.
                    </span>
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.leadId}>
                    {canManage && (
                      <td className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.leadId)}
                          onChange={() => toggleSelectLead(lead.leadId)}
                        />
                      </td>
                    )}

                    <td>
                      <Link
                        to={`/leads/${lead.leadId}`}
                        className="lead-name-cell"
                      >
                        <span className="lead-avatar">
                          {(
                            lead.firstName?.charAt(0) ||
                            'L'
                          ).toUpperCase()}
                        </span>

                        <span>
                          <strong>
                            {[lead.firstName, lead.lastName]
                              .filter(Boolean)
                              .join(' ') || 'Unnamed Lead'}
                          </strong>

                          <small>
                            ID #{lead.leadId}
                          </small>
                        </span>
                      </Link>
                    </td>

                    <td>
                      <span className="company-cell">
                        {lead.company || '—'}
                      </span>
                    </td>

                    <td>
                      <span className="phone-cell">
                        {lead.phone || '—'}
                      </span>
                    </td>

                    <td>
                      {lead.city || '—'}
                    </td>

                    <td>
                      <span className={getStatusClass(lead.status)}>
                        <span className="status-dot"></span>
                        {formatStatus(lead.status)}
                      </span>
                    </td>

                    <td>
                      <div className="lead-score-cell">
                        <span className={getScoreBadgeClass(lead.leadScore || 0)}>
                          {lead.leadScore || 0}
                        </span>
                        <span className="score-label">{getScoreLabel(lead.leadScore || 0)}</span>
                      </div>
                    </td>

                    <td>
                      <span className="agent-cell">
                        {lead.assignedAgentName || 'Unassigned'}
                      </span>
                    </td>

                    <td className="actions-column">
                      <div className="lead-row-actions">
                        <Link
                          to={`/leads/${lead.leadId}`}
                          className="row-action view"
                          title="View lead details"
                        >
                          View
                        </Link>

                        {canManage && (
                          <>
                            <button
                              className="row-action edit"
                              onClick={() => openEditLead(lead)}
                            >
                              Edit
                            </button>

                            <button
                              className="row-action delete"
                              onClick={() =>
                                confirmDelete(lead.leadId)
                              }
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && leads.length > 0 && (
          <div className="leads-pagination">
            <div>
              Showing page <strong>{page + 1}</strong>
              {totalPages > 0 && (
                <>
                  {' '}
                  of <strong>{totalPages}</strong>
                </>
              )}
            </div>

            <div className="pagination-buttons">
              <button
                onClick={() => setPage((current) => current - 1)}
                disabled={page === 0}
              >
                ← Previous
              </button>

              <span className="current-page">
                {page + 1}
              </span>

              <button
                onClick={() => setPage((current) => current + 1)}
                disabled={
                  totalPages === 0 || page >= totalPages - 1
                }
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Lead Modal */}
      {showLeadModal && canManage && (
        <div
          className="modal-overlay"
          onClick={() => setShowLeadModal(false)}
        >
          <div
            className="lead-modal lead-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>
                  {editingLead ? 'Edit Lead' : 'Add Lead'}
                </h2>

                <p>
                  {editingLead
                    ? 'Update lead information.'
                    : 'Create a new lead with all available fields.'}
                </p>
              </div>

              <button
                className="modal-close"
                onClick={() => setShowLeadModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={saveLead}>
              <div className="lead-form-section">
                <h4 className="form-section-title">Basic Information</h4>
                <div className="lead-form-grid">
                  <div className="form-field">
                    <label>First Name *</label>
                    <input
                      name="firstName"
                      value={leadForm.firstName}
                      onChange={handleLeadFormChange}
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Last Name</label>
                    <input
                      name="lastName"
                      value={leadForm.lastName}
                      onChange={handleLeadFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Phone *</label>
                    <input
                      name="phone"
                      value={leadForm.phone}
                      onChange={handleLeadFormChange}
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Alt Phone</label>
                    <input
                      name="altPhone"
                      value={leadForm.altPhone}
                      onChange={handleLeadFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Email</label>
                    <input
                      name="email"
                      value={leadForm.email}
                      onChange={handleLeadFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Company</label>
                    <input
                      name="company"
                      value={leadForm.company}
                      onChange={handleLeadFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>City</label>
                    <input
                      name="city"
                      value={leadForm.city}
                      onChange={handleLeadFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>State</label>
                    <input
                      name="state"
                      value={leadForm.state}
                      onChange={handleLeadFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Status</label>
                    <select
                      name="status"
                      value={leadForm.status}
                      onChange={handleLeadFormChange}
                    >
                      {STATUS_OPTIONS.filter(
                        (status) => status !== 'ALL'
                      ).map((status) => (
                        <option key={status} value={status}>
                          {formatStatus(status)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Category</label>
                    <input
                      name="category"
                      value={leadForm.category}
                      onChange={handleLeadFormChange}
                      placeholder="e.g. Real estate agent"
                    />
                  </div>
                </div>
              </div>

              <div className="lead-form-section">
                <h4 className="form-section-title">Address & Location</h4>
                <div className="lead-form-grid">
                  <div className="form-field form-field-full">
                    <label>Address</label>
                    <input
                      name="address"
                      value={leadForm.address}
                      onChange={handleLeadFormChange}
                    />
                  </div>

                  <div className="form-field form-field-full">
                    <label>Map URL (Bing/Google Maps)</label>
                    <input
                      name="mapUrl"
                      value={leadForm.mapUrl}
                      onChange={handleLeadFormChange}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              <div className="lead-form-section">
                <h4 className="form-section-title">Business Details</h4>
                <div className="lead-form-grid">
                  <div className="form-field">
                    <label>Rating</label>
                    <input
                      name="rating"
                      value={leadForm.rating}
                      onChange={handleLeadFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Open Hours</label>
                    <input
                      name="openHours"
                      value={leadForm.openHours}
                      onChange={handleLeadFormChange}
                      placeholder="e.g. Open · Closes 21:00"
                    />
                  </div>

                  <div className="form-field">
                    <label>Website</label>
                    <input
                      name="website"
                      value={leadForm.website}
                      onChange={handleLeadFormChange}
                      placeholder="https://..."
                    />
                  </div>

                  <div className="form-field form-field-full">
                    <label>Emails (comma separated)</label>
                    <input
                      name="emails"
                      value={leadForm.emails}
                      onChange={handleLeadFormChange}
                      placeholder="info@company.com,sales@company.com"
                    />
                  </div>
                </div>
              </div>

              <div className="lead-form-section">
                <h4 className="form-section-title">Social Media</h4>
                <div className="lead-form-grid lead-form-grid-3">
                  <div className="form-field">
                    <label>Facebook</label>
                    <input
                      name="facebook"
                      value={leadForm.facebook}
                      onChange={handleLeadFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Instagram</label>
                    <input
                      name="instagram"
                      value={leadForm.instagram}
                      onChange={handleLeadFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Twitter/X</label>
                    <input
                      name="twitter"
                      value={leadForm.twitter}
                      onChange={handleLeadFormChange}
                    />
                  </div>
                </div>
              </div>

              <div className="lead-form-section">
                <div className="form-field form-field-full">
                  <label>Notes</label>
                  <textarea
                    name="notes"
                    value={leadForm.notes}
                    onChange={handleLeadFormChange}
                    rows={3}
                    placeholder="Additional notes about this lead..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="modal-secondary-btn"
                  onClick={() => setShowLeadModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="modal-primary-btn"
                >
                  {editingLead ? 'Save Changes' : 'Create Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCsvModal && canManage && (
        <div
          className="modal-overlay"
          onClick={() => setShowCsvModal(false)}
        >
          <div
            className="lead-modal csv-import-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>Import Leads from CSV</h2>
                <p>Upload a CSV file to bulk-import leads into the database.</p>
              </div>
              <button
                className="modal-close"
                onClick={() => setShowCsvModal(false)}
              >
                ×
              </button>
            </div>

            <div className="csv-import-body">
              {/* Step 1: File Selection */}
              {!csvPreview && !csvResult && (
                <div className="csv-upload-zone">
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,.tsv,.txt"
                    onChange={handleCsvFileSelect}
                    style={{ display: 'none' }}
                  />
                  <div
                    className="csv-dropzone"
                    onClick={() => csvInputRef.current?.click()}
                  >
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="csv-upload-icon">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="12" y1="18" x2="12" y2="12" />
                      <polyline points="9 15 12 12 15 15" />
                    </svg>
                    <p className="csv-dropzone-title">Click to select CSV file</p>
                    <p className="csv-dropzone-hint">
                      Supports .csv, .tsv files with headers like Name, Address, Phone, Emails, etc.
                    </p>
                  </div>
                </div>
              )}

              {csvError && (
                <div className="csv-error-banner">
                  <span>⚠</span> {csvError}
                </div>
              )}

              {/* Step 2: Preview */}
              {csvPreview && !csvResult && (
                <div className="csv-preview-section">
                  <div className="csv-preview-info">
                    <span className="csv-file-name">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      {csvFile?.name}
                    </span>
                    <span className="csv-row-count">
                      {csvPreview.totalRows} rows detected
                    </span>
                  </div>

                  <div className="csv-preview-label">
                    <strong>Preview</strong> (showing first {csvPreview.previewRows?.length || 0} rows)
                  </div>

                  <div className="csv-preview-table-wrapper">
                    <table className="csv-preview-table">
                      <thead>
                        <tr>
                          {csvPreview.headers?.map((h, i) => (
                            <th key={i}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.previewRows?.map((row, ri) => (
                          <tr key={ri}>
                            {csvPreview.headers?.map((h, ci) => (
                              <td key={ci} title={row[h]}>
                                {(row[h] || '').length > 50 ? row[h].substring(0, 50) + '...' : row[h] || '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="csv-column-mapping">
                    <h4>Detected Column Mapping</h4>
                    <div className="csv-mapping-grid">
                      {csvPreview.headers?.map((h, i) => {
                        const mapped = getFieldMapping(h);
                        return (
                          <div key={i} className="csv-mapping-item">
                            <span className="csv-col-name">{h}</span>
                            <span className="csv-mapping-arrow">→</span>
                            <span className={`csv-field-name ${mapped ? '' : 'unmapped'}`}>
                              {mapped || 'Unmapped'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      className="modal-secondary-btn"
                      onClick={() => {
                        setCsvPreview(null);
                        setCsvFile(null);
                      }}
                    >
                      Choose Different File
                    </button>
                    <button
                      className="modal-primary-btn csv-confirm-btn"
                      onClick={confirmCsvImport}
                      disabled={csvImporting}
                    >
                      {csvImporting ? (
                        <>
                          <span className="btn-spinner"></span>
                          Importing...
                        </>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          Confirm Import ({csvPreview.totalRows} leads)
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Result */}
              {csvResult && (
                <div className="csv-result-section">
                  <div className="csv-result-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="16 8 10 16 7 13" />
                    </svg>
                  </div>
                  <h3>Import Complete!</h3>
                  <p className="csv-result-summary">
                    Successfully imported <strong>{csvResult.imported}</strong> leads
                    {csvResult.errors?.length > 0 && (
                      <>, with <span className="csv-error-count">{csvResult.errors.length} errors</span></>
                    )}
                  </p>

                  {csvResult.errors?.length > 0 && (
                    <div className="csv-result-errors">
                      <h4>Errors</h4>
                      <ul>
                        {csvResult.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="modal-footer">
                    <button
                      className="modal-primary-btn"
                      onClick={() => setShowCsvModal(false)}
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && canManage && (
        <div
          className="modal-overlay"
          onClick={() => setShowAssignModal(false)}
        >
          <div
            className="small-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>Assign Leads</h2>
                <p>
                  Assign {selectedLeads.length} selected lead
                  {selectedLeads.length > 1 ? 's' : ''}.
                </p>
              </div>

              <button
                className="modal-close"
                onClick={() => setShowAssignModal(false)}
              >
                ×
              </button>
            </div>

            <div className="form-field">
              <label>Select Agent</label>

              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
              >
                <option value="">Choose an agent</option>

                {agents.map((agent) => (
                  <option
                    key={agent.userId || agent.id}
                    value={agent.userId || agent.id}
                  >
                    {agent.fullName ||
                      agent.username ||
                      agent.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-footer">
              <button
                className="modal-secondary-btn"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </button>

              <button
                className="modal-primary-btn"
                onClick={assignLeads}
              >
                Assign Leads
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && canManage && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className="small-modal delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-icon">!</div>

            <h2>Delete Lead?</h2>

            <p>
              This action cannot be undone. Are you sure you want
              to delete this lead?
            </p>

            <div className="modal-footer">
              <button
                className="modal-secondary-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>

              <button
                className="delete-confirm-btn"
                onClick={deleteLead}
              >
                Delete Lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper: Map CSV header names to CRM lead field names for display
function getFieldMapping(header) {
  const h = header.toLowerCase().trim();
  const map = {
    'name': 'First Name / Last Name',
    'first_name': 'First Name',
    'firstname': 'First Name',
    'first name': 'First Name',
    'last_name': 'Last Name',
    'lastname': 'Last Name',
    'last name': 'Last Name',
    'phone': 'Phone',
    'phone number': 'Phone',
    'mobile': 'Phone',
    'alt_phone': 'Alt Phone',
    'email': 'Email',
    'emails': 'Emails (multi)',
    'company': 'Company',
    'company name': 'Company',
    'source': 'Source',
    'city': 'City',
    'state': 'State',
    'country': 'Country',
    'address': 'Address',
    'map_url': 'Map URL',
    'bing maps url': 'Map URL',
    'google maps url': 'Map URL',
    'rating': 'Rating',
    'category': 'Category',
    'open_hours': 'Open Hours',
    'open hours': 'Open Hours',
    'website': 'Website',
    'facebook': 'Facebook',
    'instagram': 'Instagram',
    'twitter': 'Twitter/X',
    'notes': 'Notes',
    'note': 'Notes',
  };
  return map[h] || null;
}